import { createSelector } from '@ngrx/store';
import cloneDeep from 'lodash.clonedeep';

import { TaxRegimeCode } from '@enums/tax-regime-code.enum';
import { VatSystemCode } from '@enums/vat-system-code.enum';
import { AppStoreState } from '@interfaces/app-store-state';
import { Company } from '@models/company';
import { CompanyConfig } from '@models/company-config';
import { CompanyState } from '@modules/company/company-core/store/company/company-reducer';

export const companySelector = (state: AppStoreState): CompanyState =>
  state?.company ? cloneDeep(state.company) : null;

export const companyWithoutVatSelector = createSelector(
  companySelector,
  (company: Company) => company?.vatSystem?.code === VatSystemCode.ss
);

export const companyBusinessUnitIdSelector = createSelector(
  companySelector,
  (company: Company) => company?.businessUnit?.id
);

export const companyIsMicroEnterpriseSelector = createSelector(
  companySelector,
  (company: Company) => company?.taxRegime === TaxRegimeCode.MICRO
);

export const companyIsCopiedSelector = createSelector(companySelector, (company: Company) => company?.isCopy);

export const companyHubEligibilityConnectionsSelector = createSelector(
  companySelector,
  (company: Company) => company?.hubEligibilityConnections
);

export const companyIsRebootable = createSelector(companySelector, (company: Company) => company?.rebootable);

export const companyConfigSelector = createSelector(companySelector, (company: Company) => company?.config);

export const companyCanAccessBuildingsSelector = createSelector(
  companySelector,
  (company: Company) =>
    ['GFA', 'SCI'].includes(company?.legalForm) && [TaxRegimeCode.SCIS, TaxRegimeCode.SCIC].includes(company?.taxRegime)
);

export const companyTaxRegimeSelector = createSelector(companySelector, (company: Company) => company?.taxRegime);

export const companyHasTaxRegimeSelector = createSelector(companySelector, (company: Company) => !!company?.taxRegime);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const companyHasAccess = (prop: keyof CompanyConfig) =>
  createSelector(companyConfigSelector, (config: CompanyConfig) => !!config?.[prop]);

export const companyApeCodeSelector = createSelector(companySelector, (company: Company) => company?.apeCode ?? null);
